import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  Agreements,
  commodityOptions,
  CurrenciesConversions,
  Invoices,
  SelectOptions,
  ShipmentFulfillments,
} from '@etoh/database/core';
import { UserService } from 'apps/desktop/src/app/user.service';
import copy from 'fast-copy';
import { sum } from 'lodash';

import {
  eachMonthOfInterval,
  endOfYear,
  parseISO,
  startOfYear,
} from 'date-fns';

import { combineLatest, Observable, Subject } from 'rxjs';
import { map, shareReplay, startWith, take, takeUntil } from 'rxjs/operators';
import { AgreementsService } from '../../../agreements/agreements.service';
import { OfficesService } from '../../../offices/offices.service';
import { StoreService } from '../../../store.service';
import { UsersService } from '../../../users.service';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexStroke,
  ApexTitleSubtitle,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  NgApexchartsModule,
} from 'ng-apexcharts';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  title: ApexTitleSubtitle;
  yaxis: ApexYAxis[];
  tooltip: ApexTooltip;
  stroke: ApexStroke;
};

import { CommonModule } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import { RouterModule } from '@angular/router';
import { Users, UsersBonus, UsersCosts } from '@etoh/database/angular';
import { convertCurrencyToUSD } from 'apps/desktop/src/app/currenciesConversions/pure';

@Component({
  selector: 'etoh-dashboard',
  template: `
    <section>
      <div fxLayout="row" fxLayoutAlign="center center">
        <div>
          <form nz-form nzLayout="vertical">
            <nz-form-item>
              <nz-form-label>Commodity type</nz-form-label>
              <nz-select
                [formControl]="selectedCommodity"
                nzShowSearch
                nzPlaceHolder="Select the commodity type"
                name="selectedCommodity"
              >
                @for (option of commodityOptions; track option.value) {
                <nz-option
                  [nzLabel]="option.label"
                  [nzValue]="option.value"
                ></nz-option>
                }
              </nz-select>
            </nz-form-item>
          </form>
        </div>
      </div>

      @if (usersOptions$ | async; as usersOptions) {
      <div fxLayout="row" fxLayoutAlign="space-around center">
        <div>
          <form nz-form nzLayout="vertical">
            <nz-form-item>
              <nz-form-label>My figure</nz-form-label>
              <nz-select
                [formControl]="selectedUserId"
                nzShowSearch
                nzPlaceHolder="Select the user"
                name="selectedUser"
              >
                @for (option of usersOptions; track option.value) {
                <nz-option
                  [nzLabel]="option.label"
                  [nzValue]="option.value"
                ></nz-option>
                }
              </nz-select>
            </nz-form-item>
          </form>
        </div>
        <div>
          <form nz-form nzLayout="vertical">
            <nz-form-item>
              <nz-form-label>My office</nz-form-label>
              <nz-select
                [formControl]="selectedOffice"
                name="selectedOffice"
                nzPlaceHolder="Select an office"
                nzShowSearch
              >
                @for (option of offices$ | async; track option.value) {
                <nz-option
                  [nzLabel]="option.label"
                  [nzValue]="option.value"
                ></nz-option>
                }
              </nz-select>
            </nz-form-item>
          </form>
        </div>
      </div>
      }
    </section>

    <section>
      @if (earnings$ | async; as earnings) {
      <div fxLayout="row" fxLayoutGap="8px">
        <div fxFlex>
          <div fxLayout="column" fxLayoutGap="16px">
            @for (chart of earnings.user.charts; track chart.title.text) {
            <div class="card mat-padding-2">
              <apx-chart
                [series]="chart.series"
                [chart]="chart.chart"
                [title]="chart.title"
                [xaxis]="chart.xaxis"
                [yaxis]="chart.yaxis"
                [stroke]="chart.stroke"
              ></apx-chart>
            </div>
            }
          </div>
        </div>

        <div fxFlex>
          <div fxLayout="column" fxLayoutGap="16px">
            @for (chart of earnings.office.charts; track chart.title.text) {
            <div class="card mat-padding-2">
              <apx-chart
                [series]="chart.series"
                [chart]="chart.chart"
                [title]="chart.title"
                [xaxis]="chart.xaxis"
                [yaxis]="chart.yaxis"
                [stroke]="chart.stroke"
              ></apx-chart>
            </div>
            }
          </div>
        </div>
      </div>
      } @else {
      <div class="loading">
        <nz-spin></nz-spin>
      </div>
      }
    </section>
  `,
  styleUrls: ['./dashboard.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    NzFormModule,
    FlexLayoutModule,
    NzSelectModule,
    FormsModule,
    NzSpinModule,
    ReactiveFormsModule,
    NgApexchartsModule,
    RouterModule,
  ],
})
export class DashboardComponent implements OnInit, OnDestroy {
  commodityOptions = [...commodityOptions, { label: 'All', value: 'all' }];

  usersOptions$: Observable<SelectOptions[]>;

  offices$: Observable<SelectOptions[]>;

  private storeService = inject(StoreService);
  private userService = inject(UserService);
  private officeService = inject(OfficesService);
  private usersService = inject(UsersService);
  private agreementsService = inject(AgreementsService);
  private cd = inject(ChangeDetectorRef);
  private zone = inject(NgZone);

  selectedCommodity = new FormControl(
    this.userService.getJsonField(
      'savedFormFields',
      'dashboardSummaryCommodityType'
    ) ?? 'all'
  );
  selectedUserId = new FormControl(null);
  selectedOffice = new FormControl(null);

  earnings$: Observable<{
    user: {
      charts: ChartOptions[];
    };
    office: {
      charts: ChartOptions[];
    };
  }>;

  private _destroy$ = new Subject();

  ngOnInit(): void {
    this.selectedCommodity.valueChanges
      .pipe(
        takeUntil(this._destroy$),
        map((value) => ('all' === value ? null : value))
      )
      .subscribe((value) => {
        this.userService.updateJsonbField(
          'savedFormFields',
          'dashboardSummaryCommodityType',
          value
        );
      });

    this.usersOptions$ = this.usersService.getBrokersForOptions$();
    this.selectedUserId.patchValue(this.userService.userId$.value as any);

    this.offices$ = this.officeService.getOfficesForOptions$();
    this.selectedOffice.patchValue(this.userService.userOfficeId$.value as any);

    this.userService.user$
      .pipe(
        map((user) => !(user?.isManager || user?.isAdmin)),
        take(1)
      )
      .subscribe((res) => {
        if (res) {
          this.selectedOffice.disable();
          this.selectedUserId.disable();
        }
      });

    this.earnings$ = combineLatest([
      this.storeService.store.usersBonus.data$,
      this.storeService.store.usersCosts.data$,
      this.storeService.getStoreByKeys$<Users>('users'),
      this.storeService.store.invoices.data$,
      this.storeService.store.agreements.data$,
      this.storeService.getStoreByKeys$<Agreements>('agreements'),
      this.storeService.getStoreByKeys$<ShipmentFulfillments>(
        'shipmentFulfillments'
      ),
      this.selectedCommodity.valueChanges.pipe(
        startWith(this.selectedCommodity.value),
        map((value) => ('all' === value ? null : value))
      ),
      this.selectedUserId.valueChanges.pipe(
        startWith(this.selectedUserId.value)
      ),
      this.selectedOffice.valueChanges.pipe(
        startWith(this.selectedOffice.value)
      ),
      this.storeService.store.currenciesConversions.data$,
    ]).pipe(
      map(
        ([
          usersBonus,
          usersCosts,
          usersByKey,
          invoices,
          agreements,
          agreementsByKeys,
          shipmentFulfillmentsByKeys,
          selectedCommodity,
          selectedUserId,
          selectedOffice,
          currenciesConversions,
        ]) => {
          return this.zone.runOutsideAngular(() => {
            let extendedInvoices: Invoices[] = copy(invoices) as Invoices[];

            extendedInvoices = extendedInvoices.filter(
              (invoice: Invoices) => invoice.status !== 'void'
            );

            extendedInvoices = extendedInvoices
              .map((i) =>
                this.storeService.jointObject(
                  i,
                  'agreementId',
                  'agreement',
                  agreementsByKeys
                )
              )
              .map((i) =>
                this.storeService.jointObject<Invoices>(
                  i,
                  'shipmentFulfillmentId',
                  'shipmentFulfillment',
                  shipmentFulfillmentsByKeys
                )
              );

            let extendedUsersBonus: UsersBonus[] = copy(
              usersBonus
            ) as UsersBonus[];
            let extendedUsersCosts: UsersCosts[] = copy(
              usersCosts
            ) as UsersCosts[];

            extendedUsersBonus = extendedUsersBonus.map((i) =>
              this.storeService.jointObject(i, 'usersId', 'user', usersByKey)
            );

            extendedUsersCosts = extendedUsersCosts.map((i) =>
              this.storeService.jointObject(i, 'usersId', 'user', usersByKey)
            );

            const isBrokerAgreement = (a: Agreements) =>
              a.buyerUserId === selectedUserId ||
              a.sellerUserId === selectedUserId;

            const isOfficeAgreement = (a: Agreements) =>
              a.buyerOfficeId === selectedOffice ||
              a.sellerOfficeId === selectedOffice;

            const res = {
              user: {
                charts: [
                  this.getApprovedAgreementsStatForChart(
                    agreements.filter(
                      (a) =>
                        isBrokerAgreement(a) &&
                        a.contractStatus === 'approvedByParties' &&
                        (a.commodityType === selectedCommodity ||
                          !selectedCommodity)
                    ),
                    usersBonus.filter(
                      (b: UsersBonus) => b.usersId === selectedUserId
                    ),
                    usersCosts.filter(
                      (b: UsersCosts) => b.usersId === selectedUserId
                    ),
                    currenciesConversions,
                    selectedUserId ?? undefined
                  ),
                  this.getInvoicesStatForChart(
                    extendedInvoices
                      .filter((i) => i.brokerId === selectedUserId)
                      .filter(
                        (i) =>
                          i.agreement?.commodityType === selectedCommodity ||
                          !selectedCommodity
                      ),
                    usersBonus.filter(
                      (b: UsersBonus) => b.usersId === selectedUserId
                    ),
                    usersCosts.filter(
                      (b: UsersCosts) => b.usersId === selectedUserId
                    ),
                    currenciesConversions
                  ),
                ],
              },
              office: {
                charts: [
                  this.getApprovedAgreementsStatForChart(
                    agreements.filter(
                      (a) =>
                        isOfficeAgreement(a) &&
                        a.contractStatus === 'approvedByParties' &&
                        (a.commodityType === selectedCommodity ||
                          !selectedCommodity)
                    ),
                    extendedUsersBonus.filter(
                      (ub) => ub.user.officeId === selectedOffice
                    ),
                    extendedUsersCosts.filter(
                      (ub) => ub.user.officeId === selectedOffice
                    ),
                    currenciesConversions,
                    undefined,
                    selectedOffice ?? undefined
                  ),
                  this.getInvoicesStatForChart(
                    extendedInvoices
                      .filter((i) => i.officeId === selectedOffice)
                      .filter(
                        (i) =>
                          i.agreement?.commodityType === selectedCommodity ||
                          !selectedCommodity
                      ),
                    extendedUsersBonus.filter(
                      (ub) => ub.user.officeId === selectedOffice
                    ),
                    extendedUsersCosts.filter(
                      (ub) => ub.user.officeId === selectedOffice
                    ),
                    currenciesConversions
                  ),
                ],
              },
            };

            return res;
          });
        }
      ),
      shareReplay(1)
    );
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  public getApprovedAgreementsStatForChart(
    agreements: Agreements[],
    usersBonus: UsersBonus[],
    usersCosts: UsersCosts[],
    currenciesConversions: CurrenciesConversions[],
    selectedUserId: string | undefined = undefined,
    selectedOfficeId: number | undefined = undefined
  ): ChartOptions {
    const now = new Date();
    const thisYear = now.getFullYear();

    const lastYear = now.getFullYear() - 1;

    const startOfYearDate = startOfYear(now);
    const endOfYearDate = endOfYear(now);

    const lastStartOfYearDate = startOfYear(new Date(lastYear, 0, 1));
    const lastEndOfYearDate = endOfYear(lastStartOfYearDate);

    const monthsForLabel = eachMonthOfInterval({
      start: startOfYearDate,
      end: endOfYearDate,
    });

    const getStats = (startDate: Date, endDate: Date) => {
      const months = eachMonthOfInterval({
        start: startDate,
        end: endDate,
      });

      const monthsData = months.map((month) => {
        return agreements.filter((agreement) => {
          const agreementDate = parseISO(agreement.startDate);

          return this.isSameMonthAndYear(agreementDate, month);
        });
      });

      const monthsDataSum = monthsData.map((monthAgreements) => {
        return sum(
          monthAgreements.map((i) =>
            convertCurrencyToUSD(
              currenciesConversions,
              i.startDate,
              i.commissionCurrency as 'unknown',
              this.agreementsService.getCommissionFromAgreementForSpecificUser(
                i,
                selectedUserId,
                selectedOfficeId
              ) ?? 0
            )
          )
        );
      });

      return monthsDataSum;
    };

    const thisYearStats = getStats(startOfYearDate, endOfYearDate);
    const lastYearStats = getStats(lastStartOfYearDate, lastEndOfYearDate);
    /*
    const x = monthsDataSum.reduce((acc, curr) => {
      if (acc.length === 0) {
        acc.push(curr);
        return acc;
      }
      acc.push(acc[acc.length - 1] + curr);
      return acc;
    }, []);
    */

    const xx: number[] = [];

    thisYearStats.forEach((value, index) => {
      if (index > 0) {
        xx[index] = xx[index - 1] + value;
      } else {
        xx[index] = value;
      }
    });

    const xx2: number[] = [];

    lastYearStats.forEach((value, index) => {
      if (index > 0) {
        xx2[index] = xx2[index - 1] + value;
      } else {
        xx2[index] = value;
      }
    });

    const breakEvenStats = this.getUsersCosts(
      startOfYearDate,
      endOfYearDate,
      usersCosts,
      currenciesConversions
    );
    const userBonusForTheYear = sum(
      usersBonus.map((value) => {
        return convertCurrencyToUSD(
          currenciesConversions,
          new Date().toISOString(),
          value.currency as any,
          value.amount as any
        );
      })
    );

    return {
      title: {
        text: 'Broker Commission',
      },

      chart: {
        type: 'line',
        stacked: false,
        animations: {
          enabled: false,
        },
        zoom: {
          enabled: false, // Désactiver le zoom globalement
        },
        toolbar: {
          autoSelected: 'zoom',
          tools: {
            zoom: false, // Désactiver l'outil de zoom dans la barre d'outils
            zoomin: false,
            zoomout: false,
          },
        },
      },

      series: [
        {
          name: `${thisYear}`,
          type: 'bar',
          data: thisYearStats.map((value) => {
            return value / 1000;
          }),
          color: '#aa0646',
        },

        {
          name: lastYear.toString(),
          type: 'bar',
          data: lastYearStats.map((value) => {
            return value / 1000;
          }),
          color: '#000000',
        },

        {
          name: `Σ${thisYear}`,
          type: 'line',
          data: this.cumulSum(thisYearStats).map((value) => {
            return (value as any) / 1000;
          }),
          color: '#aa0646',
        },
        {
          name: `Σ${lastYear}`,
          type: 'line',
          data: this.cumulSum(lastYearStats).map((value) => {
            return (value as any) / 1000;
          }),
          color: '#000000',
        },

        {
          name: `Break even`,
          type: 'area',
          data: this.cumulSum(breakEvenStats)
            .map((value) => {
              return (value as any) / 1000;
            })
            .map((n) => this.toNullIfZero(n)),
          color: '#a8a8a8',
        },
        {
          name: `Bonus`,
          type: 'line',
          data: new Array(12)
            .fill(userBonusForTheYear / 1000)
            .map((n) => this.toNullIfZero(n)),
          color: '#34a853',
        },
      ],
      xaxis: {
        categories: monthsForLabel.map((month) =>
          month.toLocaleString('default', { month: 'short' })
        ),
      },
      yaxis: this.getAllYAxis(`${thisYear}`, `Σ${thisYear}`),
      stroke: {
        curve: 'smooth',
        width: [0, 0, 2, 2, 2, 2],
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return '$ ' + val.toFixed(0) + ' thousands';
          },
        },
      },
    };
  }

  public getInvoicesStatForChart(
    invoices: Invoices[],
    usersBonus: UsersBonus[],
    usersCosts: UsersCosts[],
    currenciesConversions: CurrenciesConversions[]
  ): ChartOptions {
    const now = new Date();
    const thisYear = now.getFullYear();

    const lastYear = now.getFullYear() - 1;

    const startOfYearDate = startOfYear(now);
    const endOfYearDate = endOfYear(now);

    const lastStartOfYearDate = startOfYear(new Date(lastYear, 0, 1));
    const lastEndOfYearDate = endOfYear(lastStartOfYearDate);

    const monthsForLabel = eachMonthOfInterval({
      start: startOfYearDate,
      end: endOfYearDate,
    });

    const getStats = (startDate: Date, endDate: Date) => {
      const months = eachMonthOfInterval({
        start: startDate,
        end: endDate,
      });

      const monthsData = months.map((month) => {
        return invoices.filter((invoice) => {
          const invoiceDate = parseISO(invoice.createdAt);

          return this.isSameMonthAndYear(invoiceDate, month);
        });
      });

      const monthsDataSum = monthsData.map((monthInvoices) => {
        return sum(
          monthInvoices.map((i) =>
            convertCurrencyToUSD(
              currenciesConversions,
              i.createdAt,
              i.currency as any,
              i.amount as any
            )
          )
        );
      });

      return monthsDataSum;
    };

    const thisYearStats = getStats(startOfYearDate, endOfYearDate);
    const lastYearStats = getStats(lastStartOfYearDate, lastEndOfYearDate);
    /*
    const x = monthsDataSum.reduce((acc, curr) => {
      if (acc.length === 0) {
        acc.push(curr);
        return acc;
      }
      acc.push(acc[acc.length - 1] + curr);
      return acc;
    }, []);
    */

    const xx: number[] = [];

    thisYearStats.forEach((value, index) => {
      if (index > 0) {
        xx[index] = xx[index - 1] + value;
      } else {
        xx[index] = value;
      }
    });

    const xx2: number[] = [];

    lastYearStats.forEach((value, index) => {
      if (index > 0) {
        xx2[index] = xx2[index - 1] + value;
      } else {
        xx2[index] = value;
      }
    });

    const breakEvenStats = this.getUsersCosts(
      startOfYearDate,
      endOfYearDate,
      usersCosts,
      currenciesConversions
    );
    const userBonusForTheYear = sum(
      usersBonus.map((value) => {
        return convertCurrencyToUSD(
          currenciesConversions,
          new Date().toISOString(),
          value.currency as any,
          value.amount as any
        );
      })
    );

    return {
      title: {
        text: 'Invoiced',
      },

      chart: {
        type: 'line',
        stacked: false,
        animations: {
          enabled: false,
        },
        zoom: {
          enabled: false, // Désactiver le zoom globalement
        },
        toolbar: {
          autoSelected: 'zoom',
          tools: {
            zoom: false, // Désactiver l'outil de zoom dans la barre d'outils
            zoomin: false,
            zoomout: false,
          },
        },
      },

      series: [
        {
          name: `${thisYear}`,
          type: 'bar',
          data: thisYearStats.map((value) => {
            return value / 1000;
          }),
          color: '#aa0646',
        },

        {
          name: lastYear.toString(),
          type: 'bar',
          data: lastYearStats.map((value) => {
            return value / 1000;
          }),
          color: '#000000',
        },

        {
          name: `Σ${thisYear}`,
          type: 'line',
          data: this.cumulSum(thisYearStats).map((value) => {
            return (value as any) / 1000;
          }),
          color: '#aa0646',
        },
        {
          name: `Σ${lastYear}`,
          type: 'line',
          data: this.cumulSum(lastYearStats).map((value) => {
            return (value as any) / 1000;
          }),
          color: '#000000',
        },

        {
          name: `Break even`,
          type: 'area',
          data: this.cumulSum(breakEvenStats)
            .map((value) => {
              return (value as any) / 1000;
            })
            .map((n) => this.toNullIfZero(n)),
          color: '#a8a8a8',
        },
        {
          name: `Bonus`,
          type: 'line',
          data: new Array(12)
            .fill(userBonusForTheYear / 1000)
            .map((n) => this.toNullIfZero(n)),
          color: '#34a853',
        },
      ],
      xaxis: {
        categories: monthsForLabel.map((month) =>
          month.toLocaleString('default', { month: 'short' })
        ),
      },
      yaxis: this.getAllYAxis(`${thisYear}`, `Σ${thisYear}`),
      stroke: {
        curve: 'smooth',
        width: [0, 0, 2, 2, 2, 2],
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return '$ ' + val.toFixed(0) + ' thousands';
          },
        },
      },
    };
  }

  isSameMonthAndYear(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth()
    );
  }

  cumulSum(arr: number[], stopIndex: number | null = null): (number | null)[] {
    const xx: (number | null)[] = [];

    arr.forEach((value, index) => {
      if (stopIndex && index > stopIndex) {
        xx[index] = null;
        return;
      }

      if (index > 0) {
        xx[index] = (xx[index - 1] ?? 0) + value;
      } else {
        xx[index] = value;
      }
    });

    return xx;
  }

  getUsersCosts = (
    startDate: Date,
    endDate: Date,
    usersCosts: UsersCosts[],
    currenciesConversions: CurrenciesConversions[]
  ) => {
    const months = eachMonthOfInterval({
      start: startDate,
      end: endDate,
    });

    const monthsData = months.map((month) => {
      return usersCosts.filter((usersCost) => {
        const userCostDate = new Date(usersCost.year, usersCost.month - 1);

        return this.isSameMonthAndYear(userCostDate, month);
      });
    });

    const monthsDataSum = monthsData.map((monthUserCosts) => {
      return sum(
        monthUserCosts.map((i) =>
          convertCurrencyToUSD(
            currenciesConversions,
            i.createdAt,
            i.currency as 'unknown',
            i.amount
          )
        )
      );
    });

    return monthsDataSum;
  };

  private getYAxis(title: string) {
    return {
      labels: {
        formatter: function (value: any) {
          return value?.toFixed(0); // Rounds the value to the nearest whole number
        },
      },
      seriesName: title,
      show: false,
    };
  }

  public getAllYAxis(a: string, b: string): ApexYAxis[] {
    return [
      // thisYear
      {
        seriesName: a,
        labels: {
          formatter: function (value: any) {
            return value.toFixed(0); // Rounds the value to the nearest whole number
          },
        },
        title: {
          text: 'Bars (k$)',
        },
        show: true,
      },
      // lastYear
      this.getYAxis(a),
      // `Σ${lastYear}`
      {
        seriesName: b,
        labels: {
          formatter: function (value: any) {
            return value.toFixed(0); // Rounds the value to the nearest whole number
          },
        },
        opposite: true,
        title: {
          text: 'Line (k$)',
        },
        show: true,
      },
      // sum thisYear
      this.getYAxis(b),
      // breakEvenStats
      this.getYAxis(b),

      // userBonusForTheYear
      this.getYAxis(b),
    ];
  }

  private toNullIfZero(value: number): number | null {
    return value === 0 ? null : value;
  }
}
